import QRCodeStyling from 'qr-code-styling';

const QUIET_MODULES = 4;

/** Scanner-safe code: black square modules, white ground, error correction M, 4-module quiet zone, no logo. */
export const createQr = (data: string, size: number, type: 'svg' | 'canvas' = 'svg'): QRCodeStyling => {
    const options = {
        width: size,
        height: size,
        type,
        data,
        margin: 0,
        qrOptions: { errorCorrectionLevel: 'M' as const },
        dotsOptions: { color: '#000000', type: 'square' as const },
        cornersSquareOptions: { color: '#000000', type: 'square' as const },
        cornersDotOptions: { color: '#000000', type: 'square' as const },
        backgroundOptions: { color: '#ffffff' },
    };
    const count = new QRCodeStyling(options)._qr?.getModuleCount() ?? 0;
    if (count === 0) return new QRCodeStyling(options);
    // Whole-pixel modules, leftover space split into the quiet zone (never below 4 modules).
    const module = Math.floor(size / (count + 2 * QUIET_MODULES));
    return new QRCodeStyling({
        ...options,
        margin: Math.floor((size - module * count) / 2),
    });
};

export const locationUrl = (locationId: string): string => `${import.meta.env.VITE_CUSTOMER_PORTAL_URL}/${locationId}`;

/** The URL a printed code encodes. The code never changes, so renaming the label keeps printed cards valid. */
export const codeUrl = (locationId: string, code: string): string => `${locationUrl(locationId)}?c=${encodeURIComponent(code)}`;

export const slugify = (text: string): string =>
    text
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'location';

/** `part` names what the file shows, for example `table-12`, `code-K7QX2M4B` or `to-go`. */
export const fileName = (locationName: string, ext: 'png' | 'svg', part?: string): string =>
    `qckorder-${slugify(locationName)}${part ? `-${slugify(part)}` : ''}.${ext}`;

const save = (blob: Blob, name: string): void => {
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(href), 1000);
};

const PNG_SIZE = 1024;
const HEAD_HEIGHT = 190;
const CAPTION_HEIGHT = 170;
const FONT = '"Bricolage Grotesque Variable", system-ui, sans-serif';

export interface PngCard {
    venue: string;
    logoUrl?: string | null;
    /** Large line under the code (table label, or the to-go line). */
    caption?: string;
}

/** The venue logo as a bitmap, or null when it cannot be read (offline, or the storage blocks cross-origin reads). */
const loadLogo = async (url: string | null | undefined): Promise<ImageBitmap | null> => {
    if (!url) return null;
    try {
        const res = await fetch(url, { mode: 'cors' });
        return res.ok ? await createImageBitmap(await res.blob()) : null;
    } catch {
        return null;
    }
};

const fitText = (ctx: CanvasRenderingContext2D, text: string, start: number, maxWidth: number): void => {
    let px = start;
    ctx.font = `600 ${px}px ${FONT}`;
    while (px > 36 && ctx.measureText(text).width > maxWidth) {
        px -= 6;
        ctx.font = `600 ${px}px ${FONT}`;
    }
};

/** 1024 px PNG on white, laid out like the printed card: logo (or venue name), the code, the caption. */
export const downloadPng = async (url: string, name: string, card: PngCard): Promise<void> => {
    const raw = await createQr(url, PNG_SIZE, 'canvas').getRawData('png');
    if (!(raw instanceof Blob)) throw new Error('QR image unavailable');
    const [qr, logo] = await Promise.all([createImageBitmap(raw), loadLogo(card.logoUrl)]);

    const canvas = document.createElement('canvas');
    canvas.width = PNG_SIZE;
    canvas.height = HEAD_HEIGHT + PNG_SIZE + (card.caption ? CAPTION_HEIGHT : 0);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas unavailable');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (logo) {
        const scale = Math.min((HEAD_HEIGHT - 50) / logo.height, (PNG_SIZE - 120) / logo.width);
        const w = logo.width * scale;
        const h = logo.height * scale;
        ctx.drawImage(logo, (PNG_SIZE - w) / 2, (HEAD_HEIGHT - h) / 2 + 10, w, h);
    } else {
        ctx.fillStyle = '#000000';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        fitText(ctx, card.venue, 72, PNG_SIZE - 120);
        ctx.fillText(card.venue, PNG_SIZE / 2, HEAD_HEIGHT / 2 + 10);
    }
    ctx.drawImage(qr, 0, HEAD_HEIGHT);
    if (card.caption) {
        ctx.fillStyle = '#000000';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        fitText(ctx, card.caption, 110, PNG_SIZE - 80);
        ctx.fillText(card.caption, PNG_SIZE / 2, HEAD_HEIGHT + PNG_SIZE + CAPTION_HEIGHT / 2 - 20);
    }
    const out = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!out) throw new Error('PNG export failed');
    save(out, name);
};

export const downloadSvg = async (url: string, name: string): Promise<void> => {
    const raw = await createQr(url, PNG_SIZE, 'svg').getRawData('svg');
    if (!(raw instanceof Blob)) throw new Error('QR image unavailable');
    save(raw, name);
};
